import { type FC, useState } from 'react';
import { Table } from 'components';
import { RoleName, type SerializedMeterAddon } from '@enmon/commons/lib/tools';
import type { Key, SortOrder } from 'antd/lib/table/interface';
import type { TablePaginationConfig } from 'antd';
import type { SorterResult } from 'antd/es/table/interface';
import { useUserInfo } from '@/hooks/useUser';
import { useMeterModules } from 'api/hooks/meter-modules/useModules';
import { getMeterModuleTableColumnsConfig } from '../utils/getColumnsConfig';
import { useCompatibleModulesFilterWithPagination } from '../hooks/useMeterModelsFilterWithPagination';
import { useModelDetailContext } from '../store';

export const CompatibleMeterModulesTable: FC = () => {
  const { role } = useUserInfo() ?? {};
  const isMagicRole = role?.name === RoleName.Magic;

  // Context
  const { model } = useModelDetailContext();

  // State
  const { filter, pagination, setPaginationState, setFilterAndPaginationState } =
    useCompatibleModulesFilterWithPagination();
  const [sortState, setSortState] = useState<{ field: Key | null; order: SortOrder }>({
    field: null,
    order: null,
  });

  // Fetch
  const { data: modulesResponse, isFetching: isFetchingModules } = useMeterModules(
    {
      ...(filter?.search && { search: filter?.search }),
      ...(filter?.technologies && { technologies: filter?.technologies }),
      meterModels: model ? [model.id] : [],
      sort: filter?.sort,
      page: pagination.current,
      pageSize: pagination.pageSize,
    },
    {
      onSuccess: data => {
        setPaginationState({ total: data?.meta?.pagination?.total });
      },
      enabled: !!model,
    },
  );
  const modules = modulesResponse?.data ?? [];

  // Handlers
  const handleTableChange = (
    newPagination: TablePaginationConfig,
    _: any,
    sorter: SorterResult<SerializedMeterAddon> | SorterResult<SerializedMeterAddon>[],
  ) => {
    const { columnKey, order } = (Array.isArray(sorter) ? sorter[0] : sorter) ?? {};
    const newFilter = {
      ...(columnKey &&
        order && {
          sort: { [columnKey.toString()]: `${order.slice(0, -3)}` },
        }),
    };

    setSortState({ field: columnKey, order });
    setFilterAndPaginationState(newFilter, newPagination);
  };

  // Table Config
  const tableColumns = getMeterModuleTableColumnsConfig({
    isMagicRole,
    sortState,
  });

  return (
    <Table<SerializedMeterAddon>
      columns={tableColumns}
      dataSource={modules}
      loading={isFetchingModules}
      pagination={{
        ...pagination,
        showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} models`,
      }}
      rowKey={item => item.id}
      scroll={{ x: 'fit-content' }}
      onChange={handleTableChange}
    />
  );
};
